import React, { useEffect, useState } from "react";
import {
    getSafeCta,
    renderSanitizedHtmlContent,
    appendQueryParamsToExternalUrl,
} from "@qlik/qlik/src/lib/util/helpers";
import { formatDateRange, formatDate, isSameDay } from "@qlik/qlik/src/lib/util/dates";
import Link from "@qlik/qlik/src/components/Link";
import Media from "@qlik/qlik/src/components/Media";
import { useI18n } from "@qlik/utils/i18nContext";

/**
 * ContentEventCard component
 * @param {Object} props - The props for the component
 * @param {Object} props.card - The card data for the component
 * @param {string} props.className - The className for the component
 * @returns {JSX.Element} - Rendered component
 */

const ContentEventCard = ({ card, className = null }) => {
    const { title, cta, startDate, endDate, media = [], region = [] } = card ?? {};
    // Get safe CTA data
    const safeCta = getSafeCta(cta);

    // Append query params if they are available
    const url = appendQueryParamsToExternalUrl(safeCta?.externalUrl, safeCta);

    // Get the i18n information for formatting dates
    const { i18n } = useI18n();

    const [dateRange, setDateRange] = useState("");
    useEffect(() => {
        const output = formatDateRange(startDate, endDate, i18n.curr.langCode);
        setDateRange(output);
    }, [setDateRange, startDate, endDate]);

    return (
        <Link
            to={safeCta?.internalLink?.slug ?? url}
            newWindow={safeCta?.browsingContext === "New Tab"}
            className="block hover:no-underline group/resource-card lg:hover:shadow-lg rounded transition-shadow duration-300"
        >
            <div
                className={`border border-stone border-opacity-50 rounded h-full p-5 lg:p-4.75 flex flex-col ${className}`}
            >
                {/* Check if media exists */}
                {media?.[0] && (
                    // Media component
                    <div className="max-h-80 md:h-32 lg:h-45 xl:h-[194px] mb-5 lg:mb-10 overflow-hidden">
                        <Media
                            media={media?.[0]}
                            className="w-full h-full object-cover lg:group-hover/resource-card:scale-110 transition-transform duration-300"
                        />
                    </div>
                )}
                {region?.[0] && (
                    <p className="event-card__region mb-2.5 mx-2.5 lg:mx-5 text-sm font-medium leading-none text-ocean uppercase tracking-wide4">
                        {region[0].title}
                    </p>
                )}
                {startDate && (
                    <div className="event-card__date mx-2.5 lg:mx-5 mb-5 text-sm font-medium leading-none text-gray/50 uppercase tracking-wide4 h-3.5">
                        {endDate && !isSameDay(startDate, endDate)
                            ? dateRange
                            : formatDate(startDate, i18n.curr.langCode)}
                    </div>
                )}
                <p className="title-5 mx-2.5 lg:mx-5 text-gray text-base lg:text-xl leading-tight line-clamp-3">
                    {/* Set inner HTML */}
                    {/* eslint-disable-next-line react/no-danger */}
                    <span dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }} />
                </p>
                <div className="mt-auto mb-2.25 mx-2.5 flex flex-wrap gap-4 pt-7 lg:mx-5 lg:mb-5">
                    <span className="green-link text-sm lg:group-hover/resource-card:text-dark-green lg:group-hover/resource-card:after:arrow-green-dark lg:group-hover/resource-card:after:translate-x-0.75">
                        {/* Display link text */}
                        {safeCta?.text}
                    </span>
                </div>
            </div>
        </Link>
    );
};

export default ContentEventCard;
